/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { isFormulaEmpty } from '@gh/builder-shared-data';
import { State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FocusedCell, GridDefinition } from '@gh/core-ui-grid';
import { isAllTrue } from '@gh/core-util';
import { combineLatest$ } from '@gh/rx';
import { distinctUntilChanged$, filter$, first$, map$, switchMap$ } from '@gh/rx/operators';
import { isEmpty, isEqual, isNil } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import {
  getHpeCuttingListItemId,
  HpeCuttingListItem,
  HpeElementType,
  HpeUomRefItem,
} from '../../../resources/hpe-details.model';
import { HpeCommandService } from '../hpe-command.service';
import {
  HpeDetailsState,
  HpeFocusedTable,
  queryHpeEditable,
  queryHpeElement,
  queryHpeFocusedCell,
  queryHpeSelectedElementInfo,
  queryHpeUomRefItem,
} from '../hpe-details.state';
import { HpeDetailsCuttingGridDefinitionFactory } from './hpe-details-cutting-grid-definition-factory.service';

@Component({
  selector: 'gh-hpe-details-cutting-list',
  template: require('./hpe-details-cutting-list.component.html'),
  styles: [require('./hpe-details-cutting-list.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HpeDetailsCuttingGridDefinitionFactory],
})
export class HpeDetailsCuttingListComponent implements OnInit, OnDestroy {
  @Input() sourceType: HpeElementType;

  sourceId: ID;

  cuttingList$: Observable<HpeCuttingListItem[]>;
  focusedCell$: Observable<Maybe<FocusedCell>>;
  total$: Observable<Maybe<number>>;
  editable$: Observable<boolean>;

  gridDefinition: GridDefinition;
  rendered: boolean = false;

  private uom: HpeUomRefItem;
  private cuttingListSourceSubscription: Subscription;

  constructor(private changeDetectorRef: ChangeDetectorRef,
              private gridDefinitionFactory: HpeDetailsCuttingGridDefinitionFactory,
              private state: State<HpeDetailsState>,
              private commands: HpeCommandService,
              private ngZone: NgZone) {

  }

  ngOnInit(): void {
    const source$ = this.state.select(queryHpeSelectedElementInfo).pipe(
      filter$(Boolean),
      filter$(({ type }) => type === this.sourceType),
      distinctUntilChanged$(isEqual));
    const parent$ = source$.pipe(
      switchMap$((source) => this.state.select(queryHpeElement, source.type, source.id)),
      filter$(Boolean));

    this.focusedCell$ = this.state.select(queryHpeFocusedCell, HpeFocusedTable.CuttingList);
    this.cuttingList$ = parent$.pipe(map$((parent: any) => parent.cuttingList));
    this.total$ = parent$.pipe(map$((parent: any) => isEmpty(parent.cuttingList) ? void 0 : parent.quantity));

    this.editable$ = combineLatest$(
      this.state.select(queryHpeEditable),
      parent$.pipe(map$((parent: any) => isFormulaEmpty(parent.formula)))).pipe(map$(isAllTrue));

    this.cuttingListSourceSubscription = source$.subscribe((cuttingListSource) => {
      this.sourceId = cuttingListSource && cuttingListSource.id;

      this.rerenderIfUomModified();
    });
  }

  ngOnDestroy(): void {
    this.cuttingListSourceSubscription.unsubscribe();
  }

  onAdd(): void {
    this.commands.cuttingListItemAdd(this.sourceType, this.sourceId);
  }

  onDelete(cutting: HpeCuttingListItem): void {
    this.commands.cuttingListItemRemove(this.sourceType, this.sourceId, getHpeCuttingListItemId(cutting));
  }

  onFocusedCellChanged(): void {
    this.commands.resetFocusedCell();
  }

  /**
   * This method completely redraws grid if uom was changed: destroy it and draw again.
   */
  private rerenderIfUomModified(): void {
    const source = this.state.project(queryHpeElement, this.sourceType, this.sourceId);

    if (isNil(source)) {
      throw new Error('Cutting list source cannot be null');
    }

    const uom = this.state.project(queryHpeUomRefItem, source && source.uomId, 1);
    if (isNil(uom)) {
      throw new Error('UoM is undefined for this cutting list source');
    }

    if (this.uom !== uom) {
      this.uom = uom;
      if (this.rendered) {
        this.setRendered(false);
        this.ngZone.onMicrotaskEmpty.pipe(first$()).subscribe(() => this.render());
      } else {
        this.render();
      }
    }
  }

  private render(): void {
    this.gridDefinition = this.gridDefinitionFactory.create(this.uom);
    this.setRendered(true);
  }

  private setRendered(rendered: boolean): void {
    this.rendered = rendered;
    this.changeDetectorRef.markForCheck();
  }
}
